import type { LatLng } from './use-area-polygon'

/**
 * Điểm đặt nhãn trong thửa = "pole of inaccessibility" (điểm nằm sâu trong
 * thửa nhất, xa ranh giới nhất) — thuật toán polylabel của Mapbox, viết lại cho
 * một vòng đỉnh (thửa không có lỗ).
 *
 * Vì sao không lấy trung bình đỉnh hay centroid: thửa chè ở Tân Cương hay có
 * hình chữ L / lõm theo đường mòn, hai cách đó cho điểm rơi RA NGOÀI thửa và
 * nhãn "tên hộ / tên vùng" dán lên thửa hàng xóm.
 *
 * Toạ độ tính thẳng theo độ; sai số do kinh độ co lại theo vĩ độ (~7% ở 21°N)
 * không đáng kể cho việc đặt nhãn. precision mặc định 1e-5° ≈ 1 m.
 */
export function polylabel(ring: LatLng[], precision = 1e-5): LatLng {
    if (ring.length === 0) return { lat: 0, lng: 0 }
    if (ring.length < 3) {
        const lat = ring.reduce((s, p) => s + p.lat, 0) / ring.length
        const lng = ring.reduce((s, p) => s + p.lng, 0) / ring.length
        return { lat, lng }
    }

    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const p of ring) {
        minX = Math.min(minX, p.lng)
        minY = Math.min(minY, p.lat)
        maxX = Math.max(maxX, p.lng)
        maxY = Math.max(maxY, p.lat)
    }
    const width = maxX - minX
    const height = maxY - minY
    const cellSize = Math.max(Math.min(width, height), precision)
    const h = cellSize / 2

    // Hàng đợi ưu tiên đơn giản: mảng + sắp xếp theo `max` giảm dần. Số ô nhỏ
    // (thửa vài chục đỉnh) nên không cần heap.
    const queue: Cell[] = []
    for (let x = minX; x < maxX; x += cellSize) {
        for (let y = minY; y < maxY; y += cellSize) {
            queue.push(makeCell(x + h, y + h, h, ring))
        }
    }

    // Ô xuất phát: centroid, và tâm bbox nếu tốt hơn.
    let best = centroidCell(ring)
    const bboxCell = makeCell(minX + width / 2, minY + height / 2, 0, ring)
    if (bboxCell.d > best.d) best = bboxCell

    while (queue.length > 0) {
        queue.sort((a, b) => b.max - a.max)
        const cell = queue.shift() as Cell
        if (cell.d > best.d) best = cell
        // Không ô nào trong hàng đợi còn có thể tốt hơn `best` quá precision.
        if (cell.max - best.d <= precision) continue
        const q = cell.h / 2
        queue.push(makeCell(cell.x - q, cell.y - q, q, ring))
        queue.push(makeCell(cell.x + q, cell.y - q, q, ring))
        queue.push(makeCell(cell.x - q, cell.y + q, q, ring))
        queue.push(makeCell(cell.x + q, cell.y + q, q, ring))
    }
    return { lat: best.y, lng: best.x }
}

type Cell = {
    x: number
    y: number
    h: number
    /** Khoảng cách có dấu từ tâm ô tới ranh giới (âm = ngoài thửa). */
    d: number
    /** Khoảng cách tốt nhất có thể đạt trong ô. */
    max: number
}

function makeCell(x: number, y: number, h: number, ring: LatLng[]): Cell {
    const d = pointToPolygonDist(x, y, ring)
    return { x, y, h, d, max: d + h * Math.SQRT2 }
}

/** Khoảng cách có dấu từ điểm tới ranh giới thửa (dương = trong thửa). */
function pointToPolygonDist(x: number, y: number, ring: LatLng[]): number {
    let inside = false
    let minDistSq = Infinity
    for (let i = 0, len = ring.length, j = len - 1; i < len; j = i++) {
        const a = ring[i]
        const b = ring[j]
        if (
            a.lat > y !== b.lat > y &&
            x < ((b.lng - a.lng) * (y - a.lat)) / (b.lat - a.lat) + a.lng
        ) {
            inside = !inside
        }
        minDistSq = Math.min(minDistSq, segDistSq(x, y, a, b))
    }
    return (inside ? 1 : -1) * Math.sqrt(minDistSq)
}

function segDistSq(px: number, py: number, a: LatLng, b: LatLng): number {
    let x = a.lng
    let y = a.lat
    let dx = b.lng - x
    let dy = b.lat - y
    if (dx !== 0 || dy !== 0) {
        const t = ((px - x) * dx + (py - y) * dy) / (dx * dx + dy * dy)
        if (t > 1) {
            x = b.lng
            y = b.lat
        } else if (t > 0) {
            x += dx * t
            y += dy * t
        }
    }
    dx = px - x
    dy = py - y
    return dx * dx + dy * dy
}

function centroidCell(ring: LatLng[]): Cell {
    let area = 0
    let x = 0
    let y = 0
    for (let i = 0, len = ring.length, j = len - 1; i < len; j = i++) {
        const a = ring[i]
        const b = ring[j]
        const f = a.lng * b.lat - b.lng * a.lat
        x += (a.lng + b.lng) * f
        y += (a.lat + b.lat) * f
        area += f * 3
    }
    if (area === 0) return makeCell(ring[0].lng, ring[0].lat, 0, ring)
    return makeCell(x / area, y / area, 0, ring)
}

/** Điểm có nằm trong thửa không — dùng khi cần kiểm nhãn (test). */
export function pointInRing(p: LatLng, ring: LatLng[]): boolean {
    return pointToPolygonDist(p.lng, p.lat, ring) > 0
}
